import { getExportRsFileName } from '@/features/batch/_utils/get-export-rs-file-name';
import {
  ReportInvestmentFundsModel,
  ReportInvestmentFundsParamsModel,
  ReportLocalGovernmentFundsParamsModel,
  ReportLocalGovernmentFundsTableAModel,
  ReportLocalGovernmentFundsTableBModel,
  ReportOrganizingTableFilterSchema,
  ReportOrganizingTableFormat1Model,
  ReportOrganizingTableFormat2Model,
  ReportOrganizingTableFormat3Model,
  ReportOrganizingTableFormat4Model,
  ReportOrganizingTableFormat5Model,
  ReportStandardFundsForPrivateModel,
  ReportStandardFundsForPublicModel,
  ReportStandardFundsParamsModel,
  reportFormatType,
  type reportRsBatchTargetType,
} from '@/features/mold/models';
import { dateFormat, type jsonType } from '@/utils';
import type { z } from 'zod';
import {
  getInvestmentFundPdfHeader,
  getInvestmentFundPdfRowData,
} from '../investment-fund/get-pdf';
import {
  getLocalGovernmentFundTableAFooter,
  getLocalGovernmentFundTableAHeader,
  getLocalGovernmentFundTableARowData,
} from '../local-government-fund/tableA';
import {
  getLocalGovernmentFundTableBFooter,
  getLocalGovernmentFundTableBHeader,
  getLocalGovernmentFundTableBRowData,
} from '../local-government-fund/tableB';
import { getFormat1Pdf } from '../organizing-table/format1/get-pdf';
import { getFormat2Pdf } from '../organizing-table/format2/get-pdf';
import { getFormat3Pdf } from '../organizing-table/format3/get-pdf';
import {
  getForma4PdfBody,
  getFormat4PdfFooter,
  getFormat4PdfHeader,
} from '../organizing-table/format4/get-pdf';
import {
  getForma5PdfBody,
  getFormat5PdfFooter,
  getFormat5PdfHeader,
} from '../organizing-table/format5/get-pdf';
import { getReportTargetFiscalYears } from '../standard-fund/private-csv';
import {
  getStandardFundReportForPrivateFooter,
  getStandardFundReportForPrivateHeader,
  getStandardFundReportForPrivatePdfRowData,
} from '../standard-fund/private-pdf';
import {
  getStandardFundReportForPublicFooter,
  getStandardFundReportForPublicHeader,
  getStandardFundReportForPublicRowData,
} from '../standard-fund/public-pdf';
import type {
  InvestmentFundTableProps,
  LocalGovernmentFundTableProps,
  OrganizingTableFormat1Props,
  OrganizingTableFormat2Props,
  OrganizingTableFormat3Props,
  OrganizingTableFormat4Props,
  OrganizingTableFormat5Props,
  StandardFundReportProps,
} from './pdf-components-props';

type BatchTargetType = z.infer<typeof reportRsBatchTargetType>;

type Params<T extends BatchTargetType> = {
  jsonData: any;
  target: T;
  batchParams: z.infer<typeof jsonType>;
  dateCreated: string;
  isDebugMode: boolean;
};

type ReturnType<T extends BatchTargetType> =
  | {
      result: T;
      fileName: string;
      data:
        | OrganizingTableFormat1Props
        | OrganizingTableFormat2Props
        | OrganizingTableFormat3Props
        | OrganizingTableFormat4Props
        | OrganizingTableFormat5Props
        | StandardFundReportProps
        | LocalGovernmentFundTableProps
        | InvestmentFundTableProps;
    }
  | { result: 'error'; error: 'EMPTY_DATA_ERROR' };

export const generateDownloadPdfData = <T extends BatchTargetType>({
  jsonData,
  target,
  batchParams,
  dateCreated,
  isDebugMode,
}: Params<T>): ReturnType<T> => {
  const fileName = getExportRsFileName({
    target,
    jsonData,
    batchParams,
    fileType: 'pdf',
    dateCreated,
  });
  const dateByCreated = dateFormat({
    datetime: dateCreated,
    format: 'YYYY/MM/DD HH:mm',
  });

  switch (target) {
    case 'rs-report1': {
      const data = ReportOrganizingTableFormat1Model.parse(jsonData);
      if (data.policies.length === 0) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format1,
      });
      const { header, body, footer } = getFormat1Pdf({
        year: params.fiscalYear,
        data,
        isDebugMode,
        projectDetailUrlType: params.projectDetailUrlType,
      });
      return {
        result: target,
        fileName,
        data: {
          organizationNames: data.organizations.map((v) => v.name).join(', '),
          header,
          body,
          footer,
          dateByCreated,
          paperSize: 'A3',
        } satisfies OrganizingTableFormat1Props,
      };
    }
    case 'rs-report2': {
      const data = ReportOrganizingTableFormat2Model.parse(jsonData);
      if (data.policies.length === 0) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }

      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format2,
      });
      const organizationNames = data.organizations
        .map((v) => v.name)
        .join(', ');

      const { header, body, footer } = getFormat2Pdf({
        year: params.fiscalYear,
        data,
        isDebugMode,
        projectDetailUrlType: params.projectDetailUrlType,
      });

      return {
        result: target,
        fileName,
        data: {
          organizationNames,
          header,
          body,
          footer,
          dateByCreated,
          paperSize: 'A3',
        } satisfies OrganizingTableFormat2Props,
      };
    }
    case 'rs-report3': {
      const data = ReportOrganizingTableFormat3Model.parse(jsonData);
      if (!data.projects.length) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format2,
      });
      const organizationNames = data.organizations
        .map((v) => v.name)
        .join(', ');

      const { header, body, footer } = getFormat3Pdf({
        year: params.fiscalYear,
        data,
        isDebugMode,
        projectDetailUrlType: params.projectDetailUrlType,
      });

      return {
        result: target,
        fileName,
        data: {
          organizationNames,
          header,
          body,
          footer,
          dateByCreated,
          paperSize: 'A3',
        } satisfies OrganizingTableFormat3Props,
      };
    }
    case 'rs-report4': {
      const data = ReportOrganizingTableFormat4Model.parse(jsonData);
      if (!data.organizations.length) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format4,
      });
      return {
        result: target,
        fileName,
        data: {
          fiscalYear: params.fiscalYear,
          header: getFormat4PdfHeader({ currentYear: params.fiscalYear }),
          body: getForma4PdfBody({
            organizations: data.organizations,
          }),
          footer: getFormat4PdfFooter(data.totalSummaryByAccount),
          dateByCreated,
          paperSize: 'A3',
        } satisfies OrganizingTableFormat4Props,
      };
    }
    case 'rs-report5': {
      const data = ReportOrganizingTableFormat5Model.parse(jsonData);
      if (data.policies.length === 0) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format5,
      });
      const organizationNames = data.organizations
        .map((v) => v.name)
        .join(', ');
      return {
        result: target,
        fileName,
        data: {
          organizationNames,
          header: getFormat5PdfHeader({ year: params.fiscalYear }),
          body: getForma5PdfBody({
            policies: data.policies,
            isDebugMode,
            projectDetailUrlType: params.projectDetailUrlType,
          }),
          footer: getFormat5PdfFooter({ summary: data.summary }),
          dateByCreated,
          paperSize: 'A3',
        } satisfies OrganizingTableFormat5Props,
      };
    }
    case 'ks-standard-list': {
      const { fiscalYear, reportType, projectDetailUrlType } =
        ReportStandardFundsParamsModel.parse(batchParams);
      if (reportType === 'public') {
        const { data, summaryData } =
          ReportStandardFundsForPublicModel.parse(jsonData);
        if (!data.length) {
          return { result: 'error', error: 'EMPTY_DATA_ERROR' };
        }
        return {
          result: target,
          fileName,
          data: {
            fiscalYear,
            header: getStandardFundReportForPublicHeader(fiscalYear),
            rowData: getStandardFundReportForPublicRowData({
              data,
              isDebugMode,
              projectDetailUrlType,
            }),
            footer: getStandardFundReportForPublicFooter(summaryData),
            dateByCreated,
            reportType: 'public',
            paperSize: 'A3',
          } satisfies StandardFundReportProps,
        };
      }
      const { data, summaryData } =
        ReportStandardFundsForPrivateModel.parse(jsonData);
      if (!data.length) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const reportTargetFiscalYears = getReportTargetFiscalYears(fiscalYear);
      return {
        result: target,
        fileName,
        data: {
          fiscalYear,
          header: getStandardFundReportForPrivateHeader(
            fiscalYear,
            reportTargetFiscalYears,
          ),
          rowData: getStandardFundReportForPrivatePdfRowData({
            data,
            reportTargetFiscalYears,
            isDebugMode,
            projectDetailUrlType,
          }),
          footer: getStandardFundReportForPrivateFooter({
            reportTargetFiscalYears,
            summaryData,
          }),
          dateByCreated,
          reportType: 'private',
          paperSize: 'A3',
        } satisfies StandardFundReportProps,
      };
    }
    case 'ks-local-government-list-A': {
      const { data, summaryData } =
        ReportLocalGovernmentFundsTableAModel.parse(jsonData);
      if (!data.length) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const { fiscalYear, projectDetailUrlType } =
        ReportLocalGovernmentFundsParamsModel.parse(batchParams);
      return {
        result: target,
        fileName,
        data: {
          fiscalYear,
          header: getLocalGovernmentFundTableAHeader(fiscalYear),
          rowData: getLocalGovernmentFundTableARowData({
            data,
            projectDetailUrlType,
          }),
          footer: getLocalGovernmentFundTableAFooter(summaryData),
          dateByCreated,
          paperSize: 'A3',
        } satisfies LocalGovernmentFundTableProps,
      };
    }
    case 'ks-local-government-list-B': {
      const { data, summaryData } =
        ReportLocalGovernmentFundsTableBModel.parse(jsonData);
      if (!data.length) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const { fiscalYear, projectDetailUrlType } =
        ReportLocalGovernmentFundsParamsModel.parse(batchParams);
      return {
        result: target,
        fileName,
        data: {
          fiscalYear,
          header: getLocalGovernmentFundTableBHeader(fiscalYear),
          rowData: getLocalGovernmentFundTableBRowData({
            data,
            isDebugMode,
            projectDetailUrlType,
          }),
          footer: getLocalGovernmentFundTableBFooter(summaryData, isDebugMode),
          dateByCreated,
          paperSize: 'A3',
        } satisfies LocalGovernmentFundTableProps,
      };
    }
    case 'ks-investment-list': {
      const { data } = ReportInvestmentFundsModel.parse(jsonData);
      if (!data.length) {
        return { result: 'error', error: 'EMPTY_DATA_ERROR' };
      }
      const { fiscalYear, projectDetailUrlType } =
        ReportInvestmentFundsParamsModel.parse(batchParams);
      const last3FiscalYears = [fiscalYear - 2, fiscalYear - 1, fiscalYear];

      return {
        result: target,
        fileName,
        data: {
          fiscalYear,
          header: getInvestmentFundPdfHeader(last3FiscalYears),
          rowData: getInvestmentFundPdfRowData({
            data,
            isDebugMode,
            projectDetailUrlType,
          }),
          dateByCreated,
          paperSize: 'A3',
        } satisfies InvestmentFundTableProps,
      };
    }
    default: {
      const check: never = target;
      throw new Error(`Unexpected target: ${check}`);
    }
  }
};
